/**
 * @nanokart/tryon-studio-sdk
 * Mode Configuration — simplified for embedded-commerce
 */

import { StudioMode, FeatureConfig, ResolvedFeatures } from '../types/config';

// ============================================================================
// Mode Defaults
// ============================================================================

export interface ModeDefaults {
  features: FeatureConfig;
  ui: {
    showBranding: boolean;
    showLimits: boolean;
  };
}

export const MODE_DEFAULTS: Record<StudioMode, ModeDefaults> = {
  'embedded-commerce': {
    features: {
      inventoryBrowsing: true,
      multiItemSession: true,
      moments: true,
      cart: false,          // Host owns cart
      download: true,
      share: false,
      sizeRecommendation: true,
    },
    ui: {
      showBranding: true,
      showLimits: false,
    },
  },
};

// ============================================================================
// Feature Resolution
// ============================================================================

export function resolveFeatures(
  mode: StudioMode,
  explicitFeatures?: FeatureConfig
): ResolvedFeatures {
  const defaults = MODE_DEFAULTS[mode]?.features ?? MODE_DEFAULTS['embedded-commerce'].features;

  const resolved: ResolvedFeatures = {
    inventoryBrowsing: defaults.inventoryBrowsing ?? true,
    multiItemSession: defaults.multiItemSession ?? true,
    moments: defaults.moments ?? true,
    cart: defaults.cart ?? false,
    download: defaults.download ?? true,
    share: defaults.share ?? false,
    sizeRecommendation: defaults.sizeRecommendation ?? true,
  };

  if (explicitFeatures) {
    if (explicitFeatures.inventoryBrowsing !== undefined) resolved.inventoryBrowsing = explicitFeatures.inventoryBrowsing;
    if (explicitFeatures.multiItemSession !== undefined) resolved.multiItemSession = explicitFeatures.multiItemSession;
    if (explicitFeatures.moments !== undefined) resolved.moments = explicitFeatures.moments;
    if (explicitFeatures.cart !== undefined) resolved.cart = explicitFeatures.cart;
    if (explicitFeatures.download !== undefined) resolved.download = explicitFeatures.download;
    if (explicitFeatures.share !== undefined) resolved.share = explicitFeatures.share;
    if (explicitFeatures.sizeRecommendation !== undefined) resolved.sizeRecommendation = explicitFeatures.sizeRecommendation;
  }

  return resolved;
}

// ============================================================================
// UI Visibility Resolution
// ============================================================================

export function resolveUIVisibility(
  mode: StudioMode,
  explicitConfig?: { showBranding?: boolean; showLimits?: boolean }
) {
  const defaults = MODE_DEFAULTS[mode]?.ui ?? MODE_DEFAULTS['embedded-commerce'].ui;
  return {
    showBranding: explicitConfig?.showBranding ?? defaults.showBranding,
    showLimits: explicitConfig?.showLimits ?? defaults.showLimits,
  };
}

// ============================================================================
// Re-export for compatibility
// ============================================================================

export type { ModeDefaults as PartnerCapabilities };
